import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  ChevronDown,
  ChevronRight,
  BookOpen,
  Shield,
  Calendar,
  Users,
  FileText,
  Star,
  Play,
  MessageSquare,
} from 'lucide-react';

interface GuideStep {
  title: string;
  description: string;
  link?: string;
  linkLabel?: string;
}

interface GuideSection {
  id: string;
  title: string;
  icon: React.ReactNode;
  roles: string[]; // which roles see this section; empty = everyone
  steps: GuideStep[];
}

const GUIDE_SECTIONS: GuideSection[] = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: <BookOpen className="h-5 w-5" />,
    roles: [],
    steps: [
      { title: 'Sign in', description: 'Use your Yum credentials to sign in. You\'ll be assigned a role automatically.' },
      { title: 'Explore the dashboard', description: 'Your home page adapts to your role. You\'ll see events, teams, and actions relevant to you.', link: '/', linkLabel: 'Go to Dashboard' },
      { title: 'Check out Breakthrough', description: 'Review the Smart, Heart, Courage framework — it\'s embedded throughout the platform to help you think bigger.', link: '/breakthrough', linkLabel: 'View Breakthrough' },
      { title: 'Meet the personas', description: 'Understand the real users you\'re solving for. These personas will help you build something that matters.', link: '/personas', linkLabel: 'View Personas' },
    ],
  },
  {
    id: 'admin',
    title: 'Platform Administration',
    icon: <Shield className="h-5 w-5" />,
    roles: ['super_admin', 'admin'],
    steps: [
      { title: 'Create an event', description: 'Set up your hackathon with themes, scoring criteria, dates, and team settings.', link: '/events/new', linkLabel: 'Create Event' },
      { title: 'Manage users', description: 'View all users, search by name or email, and assign roles (organizer, judge, mentor).', link: '/admin/users', linkLabel: 'Manage Users' },
      { title: 'Publish the event', description: 'Once your event is configured, publish it to open registration. Participants can browse themes and form teams.' },
      { title: 'Monitor progress', description: 'Track team formation, submission progress, and scoring from the event detail page.' },
    ],
  },
  {
    id: 'organizer',
    title: 'Running Your Event',
    icon: <Calendar className="h-5 w-5" />,
    roles: ['super_admin', 'admin', 'organizer'],
    steps: [
      { title: 'Configure the event', description: 'Edit name, description, dates, and settings. Themes and scoring criteria are locked once published.' },
      { title: 'Advance the lifecycle', description: 'Move through: Draft → Open → In Progress → Submission Locked → Live Demo → Judging → Completed. Each stage unlocks different capabilities.' },
      { title: 'Lock submissions', description: 'When the deadline passes, lock submissions to freeze all edits. Teams can\'t modify their work after this.' },
      { title: 'Run Live Demo Day', description: 'Start a live session, set team order, and control the countdown timer. Judges see each team\'s submission automatically.' },
      { title: 'Finalize results', description: 'After judging, award badges, reveal the leaderboard, and finalize scores. Export results for reporting.' },
    ],
  },
  {
    id: 'participant',
    title: 'Participating in a Hackathon',
    icon: <Users className="h-5 w-5" />,
    roles: ['participant'],
    steps: [
      { title: 'Browse open events', description: 'Find events that are accepting registrations. Read the themes and choose one that inspires you.', link: '/events', linkLabel: 'Browse Events' },
      { title: 'Create or join a team', description: 'Start a new team and pick a theme, or join an existing team with an invite code.' },
      { title: 'Build your submission', description: 'Work on your project throughout the hackathon. The editor auto-saves as you type — it\'s a journey, not a one-shot form.' },
      { title: 'Add links and resources', description: 'Share your demo video URL, GitLab repo, and any other resources. These help judges understand your work.' },
      { title: 'Finalize before the deadline', description: 'When your team is ready, click "Finalize" to lock your submission for demo day. You can unlock and edit until submissions are locked by the organizer.' },
      { title: 'Present on demo day', description: 'Your team will present during the live session. Watch the timer, stay focused, and showcase what you built.' },
    ],
  },
  {
    id: 'submission',
    title: 'Building a Great Submission',
    icon: <FileText className="h-5 w-5" />,
    roles: ['participant'],
    steps: [
      { title: 'Start with the problem', description: 'Who feels this pain? How big is it? Use the Keystone Personas to ground your thinking in real user needs.', link: '/personas', linkLabel: 'View Personas' },
      { title: 'Define your solution', description: 'What did you build? How does it work? Focus on what makes it different — not just what it does.' },
      { title: 'Show the impact', description: 'Quantify outcomes. What measurable improvement does this drive? Who benefits and by how much?' },
      { title: 'Explain your technical approach', description: 'Architecture, AI usage, key decisions. Show feasibility — could this actually ship?' },
      { title: 'Link your demo and repo', description: 'A working prototype speaks louder than slides. Link your demo video and code repository.' },
      { title: 'Use Breakthrough thinking', description: 'Schedule Thinking Time. Get Whole Brained. Push and Pull. These frameworks help you go deeper.', link: '/breakthrough', linkLabel: 'View Breakthrough' },
    ],
  },
  {
    id: 'judge',
    title: 'Judging Submissions',
    icon: <Star className="h-5 w-5" />,
    roles: ['judge'],
    steps: [
      { title: 'Review the scoring criteria', description: 'Each event defines its own criteria (e.g., Impact 25%, Execution 20%, AI in Product 20%). Understand the weights before scoring.' },
      { title: 'Read each submission carefully', description: 'Review the problem statement, solution, impact, and technical approach. Click "AI Summary" for a quick analysis.' },
      { title: 'Score objectively', description: 'Use the full range (0-10). A 5 is average. Reserve 9-10 for truly exceptional work. Score each criterion independently.' },
      { title: 'Add notes', description: 'Brief qualitative feedback helps organizers and teams understand your thinking beyond the numbers.' },
      { title: 'Join Live Demo Day', description: 'During live presentations, the judge view auto-switches to the current team. Score while watching the demo.' },
    ],
  },
  {
    id: 'mentor',
    title: 'Mentoring Teams',
    icon: <MessageSquare className="h-5 w-5" />,
    roles: ['mentor'],
    steps: [
      { title: 'Check in on your teams', description: 'View assigned teams and their submission progress. Read their drafts and understand where they are.' },
      { title: 'Give structured feedback', description: 'Target a specific section (problem, solution, impact, technical). Choose the feedback type: direction, blocker, or encouragement.' },
      { title: 'Guide, don\'t build', description: 'Your role is to help teams sharpen their thinking — not to do the work for them. Ask questions that lead to better answers.' },
      { title: 'Use Breakthrough principles', description: 'Really Listen. Hold Your Position Lightly. Create a Safe Space. These help you be an effective coach.', link: '/breakthrough', linkLabel: 'View Breakthrough' },
    ],
  },
  {
    id: 'live-mode',
    title: 'Demo Day (Live Mode)',
    icon: <Play className="h-5 w-5" />,
    roles: ['super_admin', 'admin', 'organizer'],
    steps: [
      { title: 'Prepare the session', description: 'Set the team presentation order and timer duration. Only teams with finalized submissions should be included.' },
      { title: 'Start the session', description: 'Click "Start Session" to begin. The first team is queued but the timer doesn\'t start until you click "Start Timer".' },
      { title: 'Control the flow', description: 'Pause, resume, or advance to the next team. The big screen display and judge views update automatically.' },
      { title: 'Open the display', description: 'Open /events/:id/live/display in a new tab for the projector. Full-screen, no header, huge timer.' },
      { title: 'End and move to judging', description: 'When all teams have presented, end the session. This transitions the event to "Judging" state.' },
    ],
  },
];

export function GuidePage() {
  const { user, hasRole } = useAuth();
  const [expandedSection, setExpandedSection] = useState<string | null>('getting-started');

  // Filter sections based on user's roles
  const visibleSections = GUIDE_SECTIONS.filter((section) => {
    if (section.roles.length === 0) return true; // visible to everyone
    return section.roles.some((role) => hasRole(role));
  });

  return (
    <div className="animate-fade-in">
      <div className="mb-8">
        <h1 className="font-display text-3xl font-bold text-ink">Guide</h1>
        <p className="mt-2 max-w-2xl text-ink-secondary">
          Step-by-step instructions for using the platform. Content is personalized to your role
          {user?.roles && user.roles.length > 0 && (
            <> — you're seeing guides for: {user.roles.map((r) => r.replace(/_/g, ' ')).join(', ')}</>
          )}
          .
        </p>
      </div>

      <div className="space-y-3">
        {visibleSections.map((section) => {
          const isExpanded = expandedSection === section.id;
          return (
            <div key={section.id} className="card overflow-hidden">
              <button
                onClick={() => setExpandedSection(isExpanded ? null : section.id)}
                className="flex w-full items-center gap-3 p-4 text-left hover:bg-surface-sunken/50 transition-colors"
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent-subtle text-ink">
                  {section.icon}
                </div>
                <div className="flex-1">
                  <h2 className="font-semibold text-ink">{section.title}</h2>
                  <p className="text-xs text-ink-tertiary">{section.steps.length} steps</p>
                </div>
                {isExpanded ? (
                  <ChevronDown className="h-5 w-5 text-ink-tertiary" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-ink-tertiary" />
                )}
              </button>

              {isExpanded && (
                <div className="animate-fade-in border-t border-ring px-4 pb-4 pt-3">
                  <ol className="space-y-4">
                    {section.steps.map((step, i) => (
                      <li key={i} className="flex gap-3">
                        <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent text-xs font-bold text-ink-inverse mt-0.5">
                          {i + 1}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-ink">{step.title}</p>
                          <p className="mt-0.5 text-sm text-ink-secondary">{step.description}</p>
                          {step.link && (
                            <Link
                              to={step.link}
                              className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-ink-secondary hover:text-ink"
                            >
                              {step.linkLabel} →
                            </Link>
                          )}
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
